import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Spinner, Alert, Card, Row, Col, Accordion, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import { ApiUrl, Headers } from '../services/ApiUrl';
import { useUserAdminStore } from '../stores/userAdmin';

const VotantesPendientes = () => {
    const [votantesPendientes, setVotantesPendientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { userAdmin } = useUserAdminStore();

    useEffect(() => {
        if (userAdmin.login) {
            cargarVotantesPendientes();
        }
    }, [userAdmin.login]);

    const cargarVotantesPendientes = async () => {
        setLoading(true);
        setError(null);
        try {
            const url = `${ApiUrl}getvotantespendientes`;
            const response = await axios.get(url, { headers: Headers });

            if (response.data.error === false) {
                // Agrupar votantes por DNI
                const votantesAgrupados = response.data.votantes.reduce((acc, votante) => {
                    if (!acc[votante.dni]) {
                        acc[votante.dni] = {
                            dni: votante.dni,
                            nombres: [],
                            mails: [],
                            minutos_transcurridos: [],
                            recordatorios: [],
                            cantidad_registros: 0,
                            codigo_confirm: votante.codigo_confirm
                        };
                    }
                    acc[votante.dni].nombres.push(votante.nombre_apellido_votante);
                    acc[votante.dni].mails.push(votante.mail_votante);
                    acc[votante.dni].minutos_transcurridos.push(votante.minutos_transcurridos);
                    acc[votante.dni].recordatorios.push(votante.recordatorio || 0);
                    acc[votante.dni].cantidad_registros++;
                    return acc;
                }, {});

                const votantesOrdenados = Object.values(votantesAgrupados).sort((a, b) => {
                    // Tomamos el tiempo más antiguo de cada grupo
                    const maxTiempoA = Math.max(...a.minutos_transcurridos);
                    const maxTiempoB = Math.max(...b.minutos_transcurridos);
                    return maxTiempoB - maxTiempoA; // Orden descendente
                });
                setVotantesPendientes(votantesOrdenados);
            } else {
                setError(response.data.mensaje);
            }
        } catch (error) {
            setError('Error al cargar los datos: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const generarMensajeRecordatorio = (dni, codigo, email) => {
        return `Para: ${email}

**Recordatorio de Votación Pendiente**

        Notamos que iniciaste el proceso de votación para el Presupuesto Participativo pero no lo has completado aún.
Para facilitar tu participación, hemos generado un link directo que te permitirá retomar el proceso de votación:

https://trenquelauquen.gov.ar/pp/panelvotantelink?dni=${dni}&codigo=${codigo}

Si no has iniciado ningún proceso de votación, podés ignorar este correo.`;
    };

    const handleCopyMensaje = async (dni, codigo, email) => {
        try {
            const mensaje = generarMensajeRecordatorio(dni, codigo, email);
            await navigator.clipboard.writeText(mensaje);
            alert('Mensaje copiado al portapapeles');
        } catch (err) {
            alert('No se pudo copiar el mensaje. Por favor, cópielo manualmente.');
        }
    };

    const calcularTotales = () => {
        const totalDNIs = votantesPendientes.length;
        const totalRegistros = votantesPendientes.reduce((acc, votante) =>
            acc + votante.cantidad_registros, 0);
        const dnisDuplicados = votantesPendientes.filter(votante =>
            votante.cantidad_registros > 1).length;

        return { totalDNIs, totalRegistros, dnisDuplicados };
    };

    const formatearTiempoTranscurrido = (minutos) => {
        const horas = Math.floor(minutos / 60);
        const minutosRestantes = minutos % 60;
        return `${horas}h ${minutosRestantes}m`;
    };

    const handleCopyLink = async (link, email) => {
        try {
            await navigator.clipboard.writeText(link);
            alert(`Link copiado al portapapeles para ${email}`);
        } catch (err) {
            alert('No se pudo copiar el link. Por favor, cópielo manualmente.');
        }
    };

    const handleEnviarRecordatorio = async (email, nombre, dni, codigo) => {
        try {
            const response = await axios.post(`${ApiUrl}enviar-recordatorio`, {
                dni: dni,
                codigo: codigo
            }, { headers: Headers });

            if (response.data.error === false) {
                alert(`Recordatorio enviado exitosamente a ${email}`);
                // Recargamos los datos para actualizar los contadores
                cargarVotantesPendientes();
            } else {
                alert(`Error al enviar recordatorio: ${response.data.mensaje}`);
            }
        } catch (error) {
            alert(`Error al enviar recordatorio: ${error.message}`);
        }
    };

    const contenidoVotantesPendientes = () => {
        if (loading) {
            return (
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                    <p>Cargando votantes pendientes...</p>
                </div>
            );
        }

        if (error) {
            return (
                <Alert variant="danger">
                    {error}
                </Alert>
            );
        }

        const { totalDNIs, totalRegistros, dnisDuplicados } = calcularTotales();

        return (
            <>
                <Row className="mb-4">
                    <Col md={4}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <Card.Title>Total DNIs Únicos</Card.Title>
                                <h3 className="text-primary">{totalDNIs}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <Card.Title>Total Registros</Card.Title>
                                <h3 className="text-success">{totalRegistros}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <Card.Title>DNIs con Duplicados</Card.Title>
                                <h3 className="text-warning">{dnisDuplicados}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Table responsive striped bordered hover>
                    <thead>
                        <tr>
                            <th>DNI</th>
                            <th>Nombre</th>
                            <th>Email</th>
                            <th>Tiempo Pendiente</th>
                            <th>Recordatorios</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {votantesPendientes.map((votante) => (
                            <tr key={votante.dni}>
                                <td>{votante.dni}</td>
                                <td>
                                    {votante.nombres.map((nombre, i) => (
                                        <div key={i}>{nombre || '-'}</div>
                                    ))}
                                </td>
                                <td>
                                    {votante.mails.map((mail, i) => (
                                        <div key={i}>{mail || '-'}</div>
                                    ))}
                                </td>
                                <td>
                                    {votante.minutos_transcurridos.map((minutos, i) => (
                                        <div key={i}>
                                            {formatearTiempoTranscurrido(parseInt(minutos))}
                                        </div>
                                    ))}
                                </td>


                                <td>
                                    {votante.recordatorios.map((recordatorio, i) => (
                                        <div key={i}>
                                            <Badge bg={
                                                recordatorio === 0 ? "primary" :
                                                    recordatorio === 1 ? "warning" :
                                                        "danger"
                                            }>
                                                {recordatorio}
                                            </Badge>
                                        </div>
                                    ))}
                                </td>
                                <td>
    {votante.mails.map((mail, i) => {
        const link = `https://trenquelauquen.gov.ar/pp/panelvotantelink?dni=${votante.dni}&codigo=${votante.codigo_confirm}`;
        const recordatoriosEnviados = votante.recordatorios[i] || 0;

        return (
            <div key={i} className="d-flex gap-2 mb-2">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Copiar link de acceso</Tooltip>}
                >
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopyLink(link, mail)}
                    >
                        <i className="bi bi-link-45deg"></i>
                    </Button>
                </OverlayTrigger>

                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Copiar mensaje completo</Tooltip>}
                >
                    <Button
                        variant="info"
                        size="sm"
                        onClick={() => handleCopyMensaje(votante.dni, votante.codigo_confirm, mail)}
                    >
                        <i className="bi bi-chat-text"></i>
                    </Button>
                </OverlayTrigger>

                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            {recordatoriosEnviados > 0
                                ? `Recordatorios enviados: ${recordatoriosEnviados}`
                                : 'Enviar recordatorio por email'}
                        </Tooltip>
                    }
                >
                    <Button
                        variant={
                            recordatoriosEnviados === 0 ? "primary" :
                                recordatoriosEnviados === 1 ? "warning" :
                                    "danger"
                        }
                        size="sm"
                        onClick={() => handleEnviarRecordatorio(
                            mail,
                            votante.nombres[i],
                            votante.dni,
                            votante.codigo_confirm
                        )}
                    >
                        <i className="bi bi-envelope-fill"></i>
                    </Button>
                </OverlayTrigger>
            </div>
        );
    })}
</td>


                            </tr>
                        ))}
                    </tbody>
                </Table>
                {votantesPendientes.length === 0 && (
                    <Alert variant="info">
                        No hay votantes pendientes registrados.
                    </Alert>
                )}
            </>
        );
    };

    return (
        <>
            {userAdmin.login === true &&
                <Container className="mt-4">
                    <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <i className="bi bi-clock-history me-2"></i>
                                Votantes Pendientes
                            </Accordion.Header>
                            <Accordion.Body>
                                {contenidoVotantesPendientes()}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Container>
            }
        </>
    );
};

export default VotantesPendientes;